/**
 * Restrict field edits to the user stored in another field of the same record.
 * `field.editableBy` is the key of a type=reference (users) field on that model.
 */

export function isUserTypeField(field) {
  if (!field || typeof field !== "object") return false;
  const type = String(field.type || "").toLowerCase();
  const ref = String(field.ref || "").toLowerCase();
  if (type === "users" || type === "user") return true;
  return type === "reference" && (ref === "users" || ref === "user");
}

export function getUserTypeFieldsFromModel(model) {
  const fields = model && Array.isArray(model.fields) ? model.fields : [];
  const userFields = fields.filter(
    (field) => field && field.key && isUserTypeField(field)
  );
  const keys = new Set(userFields.map((field) => field.key));
  [
    { key: "createdBy", label: "CreatedBy", type: "reference", ref: "users" },
    { key: "updatedBy", label: "UpdatedBy", type: "reference", ref: "users" },
  ].forEach((extra) => {
    if (!keys.has(extra.key)) userFields.push(extra);
  });
  return userFields;
}

export function getCurrentUserId() {
  if (typeof localStorage === "undefined") return null;
  return localStorage.getItem("userId") || localStorage.getItem("_id") || null;
}

export function isCurrentUserAdmin() {
  if (typeof localStorage === "undefined") return false;
  const role = String(localStorage.getItem("role") || "").trim().toLowerCase();
  return role === "admin";
}

export function extractUserIds(value) {
  if (value == null || value === "") return [];
  if (Array.isArray(value)) {
    return value.flatMap((entry) => extractUserIds(entry));
  }
  if (typeof value === "object") {
    const id = value._id || value.id;
    return id != null && id !== "" ? [String(id)] : [];
  }
  return [String(value)];
}

/**
 * ¿Puede el usuario actual editar este campo?
 *
 * Sin nadie asignado el campo queda libre, no bloqueado: si no hay a quién
 * respetar no hay restricción que aplicar. Bloquearlo dejaba de solo lectura
 * todo lo creado antes de configurar la regla, y hacía imposible el alta
 * cuando `editableBy` apunta a un campo que solo existe después de guardar
 * (`createdBy`, que el servidor completa al crear).
 */
export function canCurrentUserEditField(
  field,
  record,
  currentUserId = getCurrentUserId()
) {
  const restrictKey = field && field.editableBy;
  if (!restrictKey) return true;
  if (isCurrentUserAdmin()) return true;
  if (!record || typeof record !== "object") return true;
  const assignedIds = extractUserIds(record[restrictKey]);
  if (!assignedIds.length) return true;
  if (!currentUserId) return false;
  return assignedIds.includes(String(currentUserId));
}
